<template>
  <div class="recurring-transactions-list-container recurring-transactions-list-main recurring-transactions-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Recurring Transactions List']" />

    <div class="glass-tab-view glass-tab-view--flush recurring-transactions-list-glass-shell">
      <div class="glass-tab-view__content recurring-transactions-list-tab-content recurring-transactions-list-page-stack">
        <div class="recurring-transactions-toolbar recurring-transactions-toolbar-glass glass-tab-panel--nested">
          <div class="recurring-transactions-list-header-actions">
            <SecondaryButton
              label="Export to CSV"
              icon="bi bi-download"
              @click="exportCSV"
            />
            <PrimaryButton
              label="Search"
              icon="bi bi-search"
              @click="showSearchModal = true"
            />
            <PrimaryButton
              label="Use"
              icon="bi bi-check-lg"
              @click="handleUse"
            />
          </div>
          <div class="recurring-transactions-list-filters recurring-transactions-list-filters-row">
            <div class="recurring-transactions-list-filter-field recurring-transactions-list-filter-field--view">
              <LabelDropdown
                label="Current View"
                select-id="recurring-transactions-list-current-view"
                v-model="currentView"
                inline
                flex
                :searchable="false"
                :options="currentViewOptions"
              />
            </div>
            <div class="recurring-transactions-list-filter-field recurring-transactions-list-filter-field--date-type">
              <LabelDropdown
                label="Date"
                select-id="recurring-transactions-list-date-type"
                v-model="dateType"
                inline
                flex
                :searchable="false"
                :options="dateTypeOptions"
              />
            </div>
            <div class="recurring-transactions-list-filter-field recurring-transactions-list-filter-field--date">
              <LabelDateField
                input-id="recurring-transactions-list-from-date"
                label="From"
                v-model="fromDate"
                placeholder="MM/DD/YYYY"
                flex
              />
            </div>
            <div class="recurring-transactions-list-filter-field recurring-transactions-list-filter-field--date">
              <LabelDateField
                input-id="recurring-transactions-list-to-date"
                label="To"
                v-model="toDate"
                placeholder="MM/DD/YYYY"
                flex
              />
            </div>
            <div class="recurring-transactions-list-filter-update">
              <PrimaryButton
                label="Update"
                icon="bi bi-arrow-clockwise"
                compact
                @click="handleUpdate"
              />
            </div>
          </div>
        </div>

        <div class="recurring-transactions-list-table-panel glass-tab-panel--nested">
          <div class="recurring-transactions-table-section consultant-glass-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table recurring-transactions-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Transaction Type</th>
                      <th>Interval</th>
                      <th>Prev. Schedule</th>
                      <th>Next Schedule</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!transactions.length">
                      <td colspan="7" class="consultant-glass-table-no-records">No records to show</td>
                    </tr>
                    <tr
                      v-for="(row, idx) in paginatedTransactions"
                      :key="row.id || idx"
                      class="consultant-glass-table-row"
                    >
                      <td>{{ row.name }}</td>
                      <td>{{ row.type }}</td>
                      <td>{{ row.transactionType }}</td>
                      <td>{{ row.interval }}</td>
                      <td>{{ row.prevSchedule }}</td>
                      <td>{{ row.nextSchedule }}</td>
                      <td
                        class="recurring-transactions-list-amount-cell"
                        :class="{ 'recurring-transactions-list-amount-cell--empty': !formatAmount(row.amount) }"
                      >{{ formatAmount(row.amount) || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="recurring-transactions-list-pagination-wrapper">
            <PaginationComponent
              select-id="recurring-transactions-list-items-per-page"
              :current-page="currentPage"
              :total-items="totalTransactions"
              :items-per-page="itemsPerPage"
              item-label="records"
              inline
              :searchable="false"
              :show-items-per-page="true"
              @page-change="handlePageChange"
              @items-per-page-change="handleItemsPerPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';

function formatTodayForDateField() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

export default {
  name: 'CompanyRecurringTransactionsList',
  components: {
    VendorDetailsSearch,
    BrucdcumHeader,
    LabelDropdown,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
    PaginationComponent,
  },
  data() {
    return {
      currentView: 'All',
      dateType: 'All',
      fromDate: formatTodayForDateField(),
      toDate: formatTodayForDateField(),
      showSearchModal: false,
      transactions: [],
      currentPage: 1,
      itemsPerPage: 10,
      currentViewOptions: [
        { value: 'All', label: 'All' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      dateTypeOptions: [
        { value: 'All', label: 'All' },
        { value: 'Prev. Schedule', label: 'Prev. Schedule' },
        { value: 'Next Schedule', label: 'Next Schedule' },
      ],
    };
  },
  computed: {
    totalTransactions() {
      return this.transactions.length;
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.transactions.slice(start, start + this.itemsPerPage);
    },
  },
  methods: {
    formatAmount(amount) {
      if (amount == null || amount === '') return '';
      const num = Number(amount);
      if (Number.isNaN(num)) return '';
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(count) {
      this.itemsPerPage = count;
      this.currentPage = 1;
    },
    handleUpdate() {
      this.currentPage = 1;
      this.$toast && this.$toast.info('Update filter coming soon!');
    },
    exportCSV() {
      this.$toast && this.$toast.info('Export to CSV coming soon!');
    },
    handleUse() {
      this.$toast && this.$toast.info('Use action coming soon!');
    },
  },
};
</script>

<style src="@/modules/company/styles/CompanyRecurringTransactionsList.css"></style>
